import React, { useState, useRef } from 'react'
import {
  Divider,
  Accordion as ExpansionPanel,
  AccordionDetails as ExpansionPanelDetails,
  Grid,
  Typography,
} from '@material-ui/core'
import RemoveQuestionButton from '../Atoms/Buttons/RemoveQuestionButton'
import EditQuestionButton from '../Atoms/Buttons/EditQuestionButton'
import RadioCheckboxTypePreview from '../Atoms/Previews/RadioCheckboxTypePreview'
import LikertTypePreview from '../Atoms/Previews/LikertTypePreview'
import RangeTypePreview from '../Atoms/Previews/RangeTypePreview'
import DropdownTypePreview from '../Atoms/Previews/DropdownTypePreview'
import NumberTypePreview from '../Atoms/Previews/NumberTypePreview'
import DatePickerTypePreview from '../Atoms/Previews/DatePickerTypePreview'
import TimePickerPreview from '../Atoms/Previews/TimePickerTypePreview'
import TextAreaTypePreview from '../Atoms/Previews/TextAreaTypePreview'
import TextFieldTypePreview from '../Atoms/Previews/TextFieldTypePreview'
import DrawingTypePreview from '../Atoms/Previews/DrawingTypePreview'
import '../index.css'
import RawPreview from '../Atoms/Previews/RawTypePreview'
import DuplicateQuestionButton from '../Atoms/Buttons/DuplicateQuestionButton'
import { ExpansionRule } from '../Organisms/ExpansionRule'
import UnsupportedQuestionTypePreview from '../Atoms/Previews/UnsupportedQuestionTypePreview'
import ExpandableTypePreview from '../Atoms/Previews/ExpandableTypePreview'
import { useDrop, useDrag } from 'react-dnd'
import DnDItemTypes from '../../features/dnd/DnDItemTypes'
import store from '../../store'
import { REORDER } from '../../features/State Management/questionsSlice'
import Box from '@material-ui/core/Box'
import useParentQuestion from '../../customHooks/useParentQuestion'

const QuestionsList = ({ index, question, ...props }) => {
  const [open, setOpen] = useState(false)

  const previewRef = useRef(null)
  const handleRef = useRef(null)

  const [, parentIndex] = useParentQuestion(question)

  const [{ handlerId }, drop] = useDrop(
    () => ({
      accept: DnDItemTypes.QUESTION,
      collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
      hover: (item, monitor) => {
        if (!previewRef.current) {
          return
        }

        const dragIndex = item.index
        const hoverIndex = index
        const dragParentIndex = item.parentIndex
        const hoverParentIndex = parentIndex

        // Don't replace items with themselves
        if (dragIndex === hoverIndex && dragParentIndex === hoverParentIndex) {
          return
        }

        // Determine rectangle on screen
        const hoverBoundingRect = previewRef.current?.getBoundingClientRect()

        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

        // Determine mouse position
        const clientOffset = monitor.getClientOffset()

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return
        }

        // Time to actually perform the action.
        store.dispatch(
          REORDER({
            source: {
              parentIndex: dragParentIndex,
              index: dragIndex,
            },
            destination: {
              parentIndex: hoverParentIndex,
              index: hoverIndex,
            },
          })
        )

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex
        item.parentIndex = hoverParentIndex
      },
    }),
    [index, parentIndex]
  )

  const [{ isDragging }, drag, preview] = useDrag({
    type: DnDItemTypes.QUESTION,
    item: () => {
      return { id: question.id, index, parentIndex }
    },
    isDragging: (monitor) => question.id === monitor.getItem().id,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drop(preview(previewRef))
  drag(handleRef)

  return (
    <Box
      sx={{ mt: 1 }}
      style={{ opacity: isDragging ? 0 : 1 }}
      id={question.title}
    >
      <ExpansionPanel expanded={open} {...props}>
        <ExpansionRule
          onClick={() => {
            if (question.type !== 'raw') {
              setOpen(!open)
            }
          }}
          question={question}
          previewRef={previewRef}
          handlerId={handlerId}
          handleRef={handleRef}
          index={index}
        />
        <Divider />
        <Details question={question} index={index} />
      </ExpansionPanel>
    </Box>
  )
}

const style = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

export default QuestionsList

const renderQuestionDetails = (question, index) => {
  switch (question.type) {
    case 'radio':
    case 'checkbox':
      return <RadioCheckboxTypePreview question={question} index={index} />
    case 'likert':
      return <LikertTypePreview question={question} index={index} />
    case 'range':
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <RangeTypePreview question={question} index={index} />
        </div>
      )
    case 'dropdown':
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <DropdownTypePreview question={question} />
        </div>
      )
    case 'textarea':
      return (
        <div style={style}>
          <TextAreaTypePreview question={question} />
        </div>
      )
    case 'number':
      return <NumberTypePreview question={question} />
    case 'date':
      return <DatePickerTypePreview question={question} />
    case 'time':
      return <TimePickerPreview question={question} />
    case 'textfield':
      return <TextFieldTypePreview question={question} />
    case 'drawing':
      return (
        <div style={style}>
          <DrawingTypePreview question={question} />
        </div>
      )
    case 'raw':
      return <RawPreview question={question} />
    case 'expandable':
      return <ExpandableTypePreview question={question} />
    default:
      return <UnsupportedQuestionTypePreview />
  }
}

const renderEditButtons = (question, index) => {
  if (question.type !== 'raw') {
    return (
      <div>
        <RemoveQuestionButton question={question} index={index} />
        <EditQuestionButton question={question} index={index} />
        <DuplicateQuestionButton question={question} index={index} />
      </div>
    )
  }
}

const Details = ({ question, index }) => {
  return (
    <ExpansionPanelDetails>
      <Grid
        container
        direction='column'
        justifyContent='center'
        alignItems='stretch'
        spacing={1}
      >
        <Grid
          item
          xs
          style={{
            textAlign: 'center',
          }}
        >
          <Typography variant='caption'>
            {question.type.toUpperCase()}
          </Typography>
        </Grid>
        <Grid
          item
          xs
          style={{
            textAlign: 'center',
          }}
        >
          {renderQuestionDetails(question, index)}
        </Grid>
        <Grid
          item
          xs
          style={{
            textAlign: 'center',
          }}
        >
          {renderEditButtons(question, index)}
        </Grid>
      </Grid>
    </ExpansionPanelDetails>
  )
}
